import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isAxiosError } from "axios"
import { JournalExportFormat } from "@/shared/api/admin-audit-log"
import {
    exportPartnerApiEvents,
    getPartnerApiEvent,
    IListPartnerApiEventsResponse,
    IPartnerApiEventFilters,
    listPartnerApiEvents,
    PartnerApiEvent,
    PartnerOperationId,
    PartnerOutcome,
    PartnerRecordKind,
} from "@/shared/api/partner-api-events"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { useDebouncedValue } from "@/shared/lib/use-debounced-value"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Pagination } from "@/shared/ui/pagination"
import { getDefaultPeriod, PeriodPicker, PeriodValue } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { JournalExportButtons } from "@/features/journal-export"
import { PageHeader } from "@/widgets/page-header"
import {
    OPERATION_OPTIONS,
    OUTCOME_OPTIONS,
    RECORD_KIND_OPTIONS,
    TARGET_TYPE_OPTIONS,
    resolveActorKindLabel,
    resolveOutcomeLabel,
    resolveRecordKindLabel,
} from "../config/labels"
import { CreatableEventTypesSelect } from "./creatable-event-types-select"
import { FilterMultiSelect } from "./filter-multi-select"

const PAGE_SIZE = 25

interface SelectOption {
    value: string
    label: string
}

interface ErrorResponse {
    error_id?: string
}

const formatDateTime = (value: string): string => {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return "—"

    return new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date)
}

const resolveRequestError = (error: unknown): string => {
    if (isAxiosError<ErrorResponse>(error)) {
        return getErrorMessage(error.response?.data?.error_id)
    }

    return getErrorMessage(undefined)
}

const splitList = (value: string): string[] =>
    value.split(",").map((entry) => entry.trim()).filter(Boolean)

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div className="min-w-0">
        <h3 className="mb-1 text-sm font-bold">{label}</h3>
        <div className="text-sm break-all">{value || "—"}</div>
    </div>
)

/**
 * Детали события ограничены allowlist-проекцией провайдера. Здесь намеренно
 * нет ни ключа, ни заголовка Authorization, ни тела запроса/ответа: этих
 * полей нет в контракте, и добавлять их сюда нельзя.
 */
const EventDetails = ({ event }: { event: PartnerApiEvent }) => (
    <div className="grid grid-cols-1 gap-4 py-3 md:grid-cols-2 lg:grid-cols-3">
        <DetailRow label="Идентификатор события" value={event.id} />
        <DetailRow label="Создано" value={formatDateTime(event.created_at)} />
        <DetailRow label="Операция" value={event.operation_id} />
        <DetailRow label="Тип события" value={event.event_type} />
        <DetailRow label="Вид записи" value={resolveRecordKindLabel(event.record_kind)} />
        <DetailRow label="Инициатор" value={resolveActorKindLabel(event.actor_kind)} />
        <DetailRow label="Идентификатор ключа" value={event.api_key_id} />
        <DetailRow label="HTTP-метод" value={event.http_method ?? ""} />
        <DetailRow label="Маршрут" value={event.route_id ?? ""} />
        <DetailRow label="Тип цели" value={event.target_type ?? ""} />
        <DetailRow label="Идентификатор цели" value={event.target_id ?? ""} />
        <DetailRow label="Класс ошибки" value={event.error_class ?? ""} />
        <DetailRow label="Исход" value={resolveOutcomeLabel(event.outcome)} />
        <DetailRow label="Идентификатор запроса" value={event.request_id ?? ""} />
        <DetailRow label="Позиция в журнале" value={event.cursor_position} />
    </div>
)

export const PartnerApiCallsPage = () => {
    const listRequestId = useRef(0)
    const detailRequestId = useRef(0)
    const [period, setPeriod] = useState<PeriodValue>(() => getDefaultPeriod())
    const [recordKinds, setRecordKinds] = useState<PartnerRecordKind[]>([])
    const [outcomes, setOutcomes] = useState<PartnerOutcome[]>([])
    const [eventTypesInput, setEventTypesInput] = useState("")
    const [operationIds, setOperationIds] = useState<PartnerOperationId[]>([])
    const [targetTypes, setTargetTypes] = useState<string[]>([])
    const [apiKeyIdsInput, setApiKeyIdsInput] = useState("")
    const [targetIdsInput, setTargetIdsInput] = useState("")
    const [page, setPage] = useState(1)
    // Снимок — транспортное состояние, а не триггер загрузки: держим его в
    // ref, чтобы запись после первой страницы не перезапускала эффект и
    // чтобы отправлялось актуальное, а не захваченное замыканием значение.
    const snapshotCursor = useRef<string | undefined>(undefined)
    const [expandedEventId, setExpandedEventId] = useState<string | null>(null)
    const [data, setData] = useState<IListPartnerApiEventsResponse | null>(null)
    const [listLoading, setListLoading] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const [detail, setDetail] = useState<PartnerApiEvent | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailError, setDetailError] = useState<string | null>(null)

    // Текстовые фильтры набираются вручную. Без задержки каждый символ
    // превращался бы в отдельный запрос к ограниченному по частоте чтению,
    // поэтому в фильтры уходит отложенное значение, а в поля — сырое.
    const debouncedApiKeyIds = useDebouncedValue(apiKeyIdsInput)
    const debouncedTargetIds = useDebouncedValue(targetIdsInput)

    // Пока хоть одно отложенное значение не сравнялось с полем, на экране и
    // в запросе разные выборки. Выгрузка в этом окне отдала бы прошлый набор.
    const filtersPending =
        debouncedApiKeyIds !== apiKeyIdsInput ||
        debouncedTargetIds !== targetIdsInput

    const filters = useMemo<IPartnerApiEventFilters>(() => ({
        periodFrom: period.from,
        periodTo: period.to,
        recordKinds,
        outcomes,
        eventTypes: splitList(eventTypesInput),
        operationIds,
        apiKeyIds: splitList(debouncedApiKeyIds),
        targetTypes,
        targetIds: splitList(debouncedTargetIds),
    }), [
        debouncedApiKeyIds,
        eventTypesInput,
        operationIds,
        debouncedTargetIds,
        targetTypes,
        outcomes,
        period.from,
        period.to,
        recordKinds,
    ])

    const fetchEvents = useCallback(async () => {
        const requestId = ++listRequestId.current
        ++detailRequestId.current
        setListLoading(true)
        setListError(null)
        setExpandedEventId(null)
        setDetail(null)
        setDetailLoading(false)
        setDetailError(null)

        try {
            const response = await listPartnerApiEvents({
                ...filters,
                page,
                pageSize: PAGE_SIZE,
                snapshotCursor: page > 1 ? snapshotCursor.current : undefined,
            })
            if (listRequestId.current !== requestId) return
            setData(response.data)
            // Каждый успешный ответ становится источником курсора для
            // следующей страницы; без него сервер отвергает page > 1.
            snapshotCursor.current = response.data.snapshot_cursor
        } catch (error) {
            if (listRequestId.current !== requestId) return
            setData(null)
            if (listRequestId.current !== requestId) return
            setListError(resolveRequestError(error))

            // Просроченный снимок иначе запирает администратора: таблица и
            // пагинация скрыты ошибкой, а «Повторить» шлёт тот же неверный
            // курсор. Возвращаем на первую страницу, где снимок берётся заново.
            if (isAxiosError<ErrorResponse>(error)
                && error.response?.data?.error_id === "partner_cursor_invalid") {
                snapshotCursor.current = undefined
                setPage(1)
            }
        } finally {
            if (listRequestId.current !== requestId) return
            setListLoading(false)
        }
    }, [filters, page])

    // Снимок гасим ровно при смене набора фильтров, а не при каждом вызове
    // обработчика: `PeriodPicker` зовёт `onChange` и при повторном выборе того
    // же пресета, и тогда действующий курсор ещё нужен — иначе следующий
    // переход на страницу 2 ушёл бы без него и получил `partner_cursor_invalid`.
    // Эффект объявлен раньше загрузки, поэтому успевает отработать до неё.
    const appliedFilters = useRef(filters)
    useEffect(() => {
        if (appliedFilters.current !== filters) {
            appliedFilters.current = filters
            snapshotCursor.current = undefined
        }
    }, [filters])

    useEffect(() => {
        fetchEvents()
    }, [fetchEvents])

    // Любое изменение фильтра обнуляет и страницу, и снимок: старый курсор
    // относится к другому набору и был бы отвергнут сервером.
    const resetPaging = () => {
        // Идентификатор списка здесь НЕ трогаем. `PeriodPicker` зовёт
        // `onChange` даже при повторном выборе того же пресета: фильтры тогда
        // не меняются, нового запроса не будет, и инвалидация оставила бы
        // страницу с вечным лоадером. Устаревание списка и так снимается тем,
        // что реальная смена фильтров запускает следующий запрос.
        ++detailRequestId.current
        setPage(1)
        setExpandedEventId(null)
        setDetail(null)
        setDetailLoading(false)
        setDetailError(null)
    }

    const handlePeriodChange = (nextPeriod: PeriodValue) => {
        setPeriod(nextPeriod)
        resetPaging()
    }

    const handleRecordKindsChange = (options: readonly SelectOption[] | null) => {
        setRecordKinds((options ?? []).map((option) => option.value as PartnerRecordKind))
        resetPaging()
    }

    const handleOutcomesChange = (options: readonly SelectOption[] | null) => {
        setOutcomes((options ?? []).map((option) => option.value as PartnerOutcome))
        resetPaging()
    }

    const handleTextFilterChange = (setValue: (value: string) => void) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setValue(event.target.value)
            resetPaging()
        }

    const handleEventTypesChange = (values: string[]) => {
        setEventTypesInput(values.join(","))
        resetPaging()
    }

    const handleOperationIdsChange = (values: string[]) => {
        // Варианты приходят из закрытого перечисления контракта, поэтому
        // сужение типа здесь не догадка: чужому значению неоткуда взяться.
        setOperationIds(values as PartnerOperationId[])
        resetPaging()
    }

    const handleTargetTypesChange = (values: string[]) => {
        setTargetTypes(values)
        resetPaging()
    }

    const handleExport = useCallback(
        (format: JournalExportFormat) => exportPartnerApiEvents(filters, format),
        [filters]
    )

    const exportFileName = useCallback(
        (format: JournalExportFormat) => `partner-api-calls.${format}`,
        []
    )

    const handleEventClick = async (eventId: string) => {
        if (expandedEventId === eventId) {
            ++detailRequestId.current
            setExpandedEventId(null)
            setDetail(null)
            setDetailLoading(false)
            setDetailError(null)
            return
        }

        const requestId = ++detailRequestId.current
        setExpandedEventId(eventId)
        setDetail(null)
        setDetailError(null)
        setDetailLoading(true)

        try {
            const response = await getPartnerApiEvent(eventId)
            if (detailRequestId.current !== requestId) return
            setDetail(response.data)
        } catch (error) {
            if (detailRequestId.current !== requestId) return
            setDetailError(resolveRequestError(error))
        } finally {
            if (detailRequestId.current === requestId) setDetailLoading(false)
        }
    }

    const selectedRecordKinds = useMemo(
        () => RECORD_KIND_OPTIONS.filter((option) => recordKinds.includes(option.value)),
        [recordKinds]
    )
    const selectedOutcomes = useMemo(
        () => OUTCOME_OPTIONS.filter((option) => outcomes.includes(option.value)),
        [outcomes]
    )

    const totalCount = data?.total ?? 0
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

    // Здесь остались только свободные идентификаторы: их перечислить нельзя,
    // поэтому они по-прежнему набираются вручную и отложены во времени.
    const textFilters: { label: string; value: string; set: (value: string) => void }[] = [
        { label: "Идентификаторы ключей", value: apiKeyIdsInput, set: setApiKeyIdsInput },
        { label: "Идентификаторы целей", value: targetIdsInput, set: setTargetIdsInput },
    ]

    return (
        <div className="_page_container">
            <PageHeader title="Вызовы партнёрского API" />
            <JournalExportButtons
                onExport={handleExport}
                fallbackFileName={exportFileName}
                filtersPending={filtersPending}
            />
            <div className="flexbox">
                <div className="box">
                    <h2 className="mb-5 text-lg font-bold screen:text-2xl">Фильтры</h2>
                    <PeriodPicker
                        value={period}
                        onChange={handlePeriodChange}
                        withInterval={false}
                    />
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <FilterMultiSelect
                            ariaLabel="Операции"
                            placeholder="Все операции"
                            options={OPERATION_OPTIONS}
                            values={operationIds}
                            onChange={handleOperationIdsChange}
                        />
                        <CreatableEventTypesSelect
                            values={splitList(eventTypesInput)}
                            onChange={handleEventTypesChange}
                        />
                        <Select
                            isMulti
                            options={RECORD_KIND_OPTIONS}
                            value={selectedRecordKinds}
                            placeholder="Все виды записей"
                            onChange={handleRecordKindsChange}
                        />
                        <Select
                            isMulti
                            options={OUTCOME_OPTIONS}
                            value={selectedOutcomes}
                            placeholder="Все исходы"
                            onChange={handleOutcomesChange}
                        />
                        <FilterMultiSelect
                            ariaLabel="Типы целей"
                            placeholder="Все типы целей"
                            options={TARGET_TYPE_OPTIONS}
                            values={targetTypes}
                            isCreatable
                            onChange={handleTargetTypesChange}
                        />
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {textFilters.map((filter) => (
                            <label key={filter.label} className="flex flex-col gap-1">
                                <span className="text-sm font-semibold text-[#828282]">
                                    {filter.label}
                                </span>
                                <input
                                    type="text"
                                    value={filter.value}
                                    onChange={handleTextFilterChange(filter.set)}
                                    placeholder="Через запятую"
                                    className="h-10 rounded-xl border border-[#B9C9C6] px-3 text-sm"
                                />
                            </label>
                        ))}
                    </div>
                </div>

                {listLoading && (
                    <div className="info_panel flex min-h-24 items-center justify-center">
                        <Loader />
                    </div>
                )}

                {!listLoading && listError && (
                    <div className="info_panel flex flex-wrap items-center justify-between gap-4">
                        <span className="text-color-danger">{listError}</span>
                        <button
                            type="button"
                            onClick={fetchEvents}
                            className="form_handle_button !w-auto px-6"
                        >
                            Повторить
                        </button>
                    </div>
                )}

                {!listLoading && !listError && data?.items.length === 0 && (
                    <NoTableData label="Нет вызовов за выбранный период" />
                )}

                {!listLoading && !listError && Boolean(data?.items.length) && (
                    <div className="table_box">
                        <div className="overflow-auto">
                            <table className="react-table min-w-[1050px]">
                                <thead>
                                    <tr>
                                        <th className="!text-left">Дата / время</th>
                                        <th className="!text-left">Операция</th>
                                        <th className="!text-left">Вид записи</th>
                                        <th className="!text-left">Ключ</th>
                                        <th className="!text-left">Цель</th>
                                        <th className="!text-left">Исход</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data?.items.map((event) => {
                                        const isExpanded = expandedEventId === event.id
                                        const target = [event.target_type, event.target_id]
                                            .filter(Boolean)
                                            .join(" / ") || "—"

                                        return (
                                            <Fragment key={event.id}>
                                                <tr
                                                    onClick={() => void handleEventClick(event.id)}
                                                    onKeyDown={(keyboardEvent) => {
                                                        if (keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
                                                            keyboardEvent.preventDefault()
                                                            void handleEventClick(event.id)
                                                        }
                                                    }}
                                                    tabIndex={0}
                                                    aria-expanded={isExpanded}
                                                    className="cursor-pointer transition hover:bg-[#F7F8FA]"
                                                >
                                                    <td>{formatDateTime(event.created_at)}</td>
                                                    <td className="!whitespace-normal break-all">
                                                        {event.operation_id}
                                                    </td>
                                                    <td className="!whitespace-normal">
                                                        {resolveRecordKindLabel(event.record_kind)}
                                                    </td>
                                                    <td
                                                        // Идентификатор ключа не секрет и показывается
                                                        // целиком: усечение до префикса склеило бы два
                                                        // разных ключа с общим началом.
                                                        className="!whitespace-normal break-all font-mono"
                                                    >
                                                        {event.api_key_id}
                                                    </td>
                                                    <td className="!whitespace-normal break-all">{target}</td>
                                                    <td className="!whitespace-normal">
                                                        <span className={event.outcome === "error" ? "text-color-danger" : undefined}>
                                                            {resolveOutcomeLabel(event.outcome)}
                                                        </span>
                                                        {event.outcome === "error" && event.error_class && (
                                                            <span className="ml-2 text-color-danger">
                                                                {event.error_class}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td
                                                            colSpan={6}
                                                            className="!whitespace-normal !bg-[#F7F8FA] !px-5"
                                                        >
                                                            {detailLoading && (
                                                                <div className="flex min-h-20 items-center justify-center"><Loader /></div>
                                                            )}
                                                            {!detailLoading && detailError && (
                                                                <div role="alert" className="py-3 text-color-danger">{detailError}</div>
                                                            )}
                                                            {!detailLoading && detail?.id === event.id && (
                                                                <EventDetails event={detail} />
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={data?.page ?? page}
                            totalPages={totalPages}
                            totalCount={totalCount}
                            setPage={setPage}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}
